import { ReactNode, useId } from 'react';

import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MUISelect,
  SelectChangeEvent,
} from '@mui/material';

import { OptionContent } from './index.styles';

export interface SelectOption {
  value: string;
  label: ReactNode;
  /** Rendered before the label, in the menu and in the closed field alike. */
  icon?: ReactNode;
}

export interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (newValue: string) => void;
  /**
   * Names the field: shown as its floating label and read as its accessible
   * name.
   */
  label?: string;
  /**
   * Keeps the label out of the field, leaving it as the accessible name alone.
   * For a select something beside it already names — a filter row's own label,
   * a column heading, a tab row that carries no label at any width.
   */
  hideLabel?: boolean;
  fullWidth?: boolean;
  /** Widest the field grows to. Ignored while `fullWidth`. */
  width?: number;
  disabled?: boolean;
  /** A line under the field, read out with it. */
  helperText?: string;
}

/** Standard field width, shared with `MultipleSelect` so filter rows line up. */
export const SELECT_WIDTH = 300;

/**
 * Single-select dropdown: the one-of-many counterpart to `MultipleSelect`,
 * carrying the same outlined field and floating label so every select in a
 * filter row reads as the same control.
 */
const Select = ({
  options,
  value,
  onChange,
  label,
  hideLabel = false,
  fullWidth = false,
  width = SELECT_WIDTH,
  disabled = false,
  helperText,
}: SelectProps) => {
  const labelId = useId();
  const helperId = useId();

  const showLabel = !!label && !hideLabel;

  return (
    <FormControl
      size='small'
      fullWidth={fullWidth}
      disabled={disabled}
      // A cap rather than a fixed width, so a select squeezes into what is left
      // of a narrow row instead of dropping below the label that names it
      sx={fullWidth ? undefined : { width: '100%', maxWidth: width }}
    >
      {showLabel && <InputLabel id={labelId}>{label}</InputLabel>}
      <MUISelect
        labelId={showLabel ? labelId : undefined}
        label={showLabel ? label : undefined}
        // Select spreads its own props onto the outlined field's root, so the
        // name and the helper have to travel through inputProps to reach the
        // combobox itself
        inputProps={{
          'aria-label': showLabel ? undefined : label,
          'aria-describedby': helperText ? helperId : undefined,
        }}
        value={value}
        onChange={(event: SelectChangeEvent<string>) => onChange(event.target.value)}
      >
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            <OptionContent>
              {option.icon}
              {option.label}
            </OptionContent>
          </MenuItem>
        ))}
      </MUISelect>
      {helperText && <FormHelperText id={helperId}>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default Select;
